import moment from 'moment';
import { observer } from 'mobx-react';
import { observable, action, computed, toJS } from 'mobx';
import _ from 'lodash';
import { militaryToNot } from './timeFormatHelpers';

export default function buildReservationDays(reservations, blockedDates) {
  const days = {};

  reservations.forEach(x => {
    const start = moment(x.checkIn);
    const end = moment(x.checkOut);
    const startDayStr = start.format('YYYY-MM-DD');
    const endDayStr = end.format('YYYY-MM-DD');
    const pending = x.reservationState === 'pending';

    const startDay = days[startDayStr]
      ? days[startDayStr]
      : new DayObj(false, start.date(), start.month() + 1, start.year());
    const endDay = days[endDayStr] || new DayObj(false, end.date(), end.month() + 1, end.year());

    if (startDayStr === endDayStr) {
      if (moment(x.checkIn).isSame(x.checkOut, 'minute')) {
        startDay.blockWholeDay(x.id, pending);
      } else {
        startDay.blockBetween(x.checkIn, x.checkOut, x.id, pending);
      }
    } else {
      startDay.blockFrom(x.checkIn, x.id, pending);
      // console.log(endDay.day, endDay.month, endDay.year);
      endDay.blockUntil(x.checkOut, x.id, pending);
    }

    if (!days[startDayStr]) {
      days[startDayStr] = startDay;
    }

    if (!days[endDayStr]) {
      days[endDayStr] = endDay;
    }
  });

  blockedDates.forEach(x => {
    const day = moment(x.blockedDates);
    const dayStr = day.format('YYYY-MM-DD');
    if (!days[dayStr]) {
      const d = new DayObj(true, day.date(), day.month() + 1, day.year());
      days[dayStr] = d;
    } else {
      days[dayStr].isBlocked = true;
    }
  });

  const sorted = [];
  const sortedKeys = _.sortBy(Object.keys(days), x => new Date(x));
  if (sortedKeys.length > 0) {
    let dayTracker = moment(sortedKeys[0]);
    let dayTrackerEnd = moment(sortedKeys[sortedKeys.length - 1]);
    while (dayTracker <= dayTrackerEnd) {
      const cDate = dayTracker.format('YYYY-MM-DD');
      const existingDate = days[cDate];
      if (!existingDate) {
        // console.log('Adding date', cDate);
        days[cDate] = new DayObj(
          false,
          dayTracker.date(),
          dayTracker.month() + 1,
          dayTracker.year(),
        );
      }
      sorted.push(days[cDate]);
      dayTracker.add(1, 'day');
    }
  }
  return {
    days,
    sorted,
  };
}

export class DayObj {
  @observable isBlocked = false;
  @observable timeBlocks = {};
  @observable day = '';
  @observable month = '';
  @observable year = '';
  @observable isWholeDay = false;
  constructor(isBlocked, day, month, year) {
    this.isBlocked = isBlocked;
    this.day = day;
    this.month = month;
    this.year = year;

    _.times(24 * 4, n => {
      const hourF = Math.floor(n / 4);
      const minuteF = (n % 4) * 15;
      this.timeBlocks[n] = {
        order: n,
        time: hourF + ':' + minuteF,
        date: this.dayStr + ' ' + hourF + ':' + minuteF,
      };
    });
  }

  @computed
  get dayStr() {
    return this.year + '-' + this.month + '-' + this.day;
  }

  @computed
  get readableMonthStr() {
    return moment(this.year + '-' + this.month + '-' + this.day).format('ddd MMM DD');
  }

  @action.bound
  blockUntil(time, reservationId, pending) {
    _.chain(this.timeBlocks)
      .filter(x => moment(x.date) <= moment(time))
      .forEach(x => {
        x.reservationId = reservationId;
        x.pending = pending;
      })
      .value();
  }

  @action.bound
  blockBetween(start, end, reservationId, pending) {
    _.chain(this.timeBlocks)
      .filter(x => moment(x.date) >= moment(start) && moment(x.date) <= moment(end))
      .forEach(x => {
        x.reservationId = reservationId;
        x.pending = pending;
      })
      .value();
  }

  @action.bound
  blockFrom(time, reservationId, pending) {
    const pt1 = _.chain(this.timeBlocks).filter(x => moment(x.date) >= moment(time));
    // const v1 = pt1.value().map(x => toJS(x));

    pt1
      .forEach(x => {
        x.reservationId = reservationId;
        x.pending = pending;
      })
      .value();
  }

  @action.bound
  blockWholeDay(reservationId, pending) {
    this.isWholeDay = true;
    const pt1 = _.chain(this.timeBlocks);
    // const v1 = pt1.value().map(x => toJS(x));

    pt1
      .forEach(x => {
        x.reservationId = reservationId;
        x.pending = pending;
      })
      .value();
  }
}

export function timeToTimeBlocks(time) {
  const timeSplit = time.split(':');
  const baseCount = +timeSplit[0] * 4;
  const minuteCount = +timeSplit[1] / 15;
  return baseCount + minuteCount;
}

function formatDate(dateStr) {
  const sp = dateStr.split('-');

  const newDateOrder = [sp[1], sp[2], sp[0]];

  return newDateOrder.join('/');
}

export function blockedDatesToMessages(blockedDates, reservationState) {
  const group = _.groupBy(blockedDates, 'reservationId');
  const messages = [];

  _.forEach(group, (v, k) => {
    const dates = v.filter(d => d.pickupTime !== null || d.returnTime !== null); //?

    const isPending = reservationState === 'pending';
    const reservationText = isPending ? 'Pending request' : 'Item reserved';

    // if start date and endate are on same day show string with both
    // else show start date and end date as joined string

    if (dates.length == 1) {
      const date = dates[0];
      if (!date.pickupTime || !date.returnTime) {
        //error?
      }
      const message =
        `${reservationText} from ` +
        militaryToNot(date.pickupTime) +
        ' to ' +
        militaryToNot(date.returnTime) +
        ' on ' +
        formatDate(date.blockedDates);
      messages.push(message);
    } else if (dates.length == 2) {
      const startDate = dates.filter(d => d.pickupTime)[0];
      const endDate = dates.filter(d => d.returnTime)[0];

      const message =
        `${reservationText} from ${formatDate(startDate.blockedDates)} at ` +
        militaryToNot(startDate.pickupTime) +
        ` to ${formatDate(endDate.blockedDates)} at ` +
        militaryToNot(endDate.returnTime);
      messages.push(message);
    }
  });
  return messages;
}



// WEBPACK FOOTER //
// ./src/helpers/resevationTimesHelper.js